import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import DateRangeSelector from '@app/components/DateRangeSelector'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import ListingTypeSelector from '@app/components/ListingTypeSelector'
import { ISoldProperty } from '@app/models/SoldProperty'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatDate,
  formatNumber,
  humanize,
} from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label } from 'reactstrap'
import {
  actionCreators,
  ISoldPropertiesListState,
} from '../modules/soldPropertiesList'

type SoldPropertiesListPageProps = ISoldPropertiesListState &
  IGlobalConfigurationState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class SoldPropertiesListPage extends React.PureComponent<SoldPropertiesListPageProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: SoldPropertiesListPageProps) {
    super(props)
    bindFunctions(this)
    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <DocumentTitle title='Sold Properties Report'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>Sold Properties</h3>
          </div>
          <AdvancedSearch className='mt-1' expand='md'>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>Date Range</Label>
                <DateRangeSelector
                  bsSize='sm'
                  value={this.props.dateRange}
                  onChange={this._onChangeDateRange}
                />
              </FormGroup>
            </Col>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>Listing Type</Label>
                <ListingTypeSelector
                  bsSize='sm'
                  includeAll
                  value={this.props.listingType}
                  onChange={this._onChangeListingType}
                />
              </FormGroup>
            </Col>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>Sort By</Label>
                <Input
                  type='select'
                  bsSize='sm'
                  value={this.props.sortOrder}
                  onChange={this._onChangeSortOrder}
                >
                  {this.props.configuration.soldPropertiesSortOptions
                    .toArray()
                    .map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                </Input>
              </FormGroup>
            </Col>
            <BidirectionalSort
              md={4}
              xl={3}
              orderBy={this.props.orderBy}
              changeOrdering={this._onChangeOrderBy}
            />
          </AdvancedSearch>
          <div className='mt-4'>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadSoldProperties}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              ref={this.tableRef}
              totalItems={this.props.totalItems}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty

                  return (
                    <div>
                      <div
                        className='overflow-text font-weight-semi-bold'
                        title={soldProperty.name}
                      >
                        {soldProperty.name}
                      </div>
                      <div
                        className='overflow-text small'
                        title={formatAddressSingleLine(soldProperty.address)}
                      >
                        {formatAddressSingleLine(soldProperty.address)}
                      </div>
                    </div>
                  )
                }}
                dataKey='name'
                flexGrow={1}
                label='Property'
                width={250}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return humanize(soldProperty.listingType)
                }}
                dataKey='listingType'
                label='Type'
                width={60}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return formatDate(soldProperty.listingDate, 'MM/DD/YYYY')
                }}
                dataKey='listingDate'
                label='Listing Date'
                width={115}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return formatDate(soldProperty.contractDate, 'MM/DD/YYYY')
                }}
                dataKey='contractDate'
                label='Contract Date'
                width={115}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return formatDate(soldProperty.closingDate, 'MM/DD/YYYY')
                }}
                dataKey='closingDate'
                label='Closing Date'
                width={115}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return formatNumber(soldProperty.daysOnMarket)
                }}
                className='text-right'
                dataKey='daysOnMarket'
                label='Days on Market'
                width={120}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const soldProperty = rowData as ISoldProperty
                  return formatNumber(soldProperty.daysToClose)
                }}
                className='text-right'
                dataKey='daysToClose'
                label='Days to Close'
                width={115}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _onChangeDateRange(dateRange: string) {
    this.props.setDateRange(dateRange)
    this._reloadData()
  }

  private _onChangeListingType(newListingType: string) {
    this.props.setListingType(newListingType)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _noRowsRenderer() {
    return <div>No listings</div>
  }

  private _onRowClicked(item: ISoldProperty) {
    window.open(
      `${window.location.pathname.replace(
        `admin/reports/soldProperties`,
        `brokerage/pipeline/${item.pipelineId}`,
      )}`,
    )
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
